/**
 * feat-299 — every new on-screen string the inline editor on `/quiz-review`
 * puts on screen, in one place (design NOTES §9).
 *
 * Same reason as `QUIZ_REVIEW_NAV_COPY`: a literal-text grep for copy that is
 * visibly on screen returns a true zero when the copy is inlined in JSX. Tests
 * assert against these constants, not against retyped strings.
 *
 * Strings that ALREADY exist are imported where they are used, never retyped
 * here: `AUTO_SAVE_COPY.saving / .saved / .tryAgain / .couldNotSaveField /
 * .signInExpired / .signInAgainThen` and `APPROVAL_EDITOR_COPY.headerBadge /
 * .queueLink / .treeChipNeverApprovedLabel`, `APPROVAL_QUEUE_COPY.neverApprovedChip`.
 *
 * Every hierarchy word goes through `QUIZ_TERMS`.
 */
import { QUIZ_TERMS } from '../quizTerminology';
import { APPROVAL_EDITOR_COPY } from '../curriculum/approvalCopy';

const T = QUIZ_TERMS;

/** Upper-case the first letter, for a label that starts a sentence. */
export const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const OPENS_APPROVALS = 'Opens Approvals.';

export const QUIZ_REVIEW_EDIT_COPY = {
  // --- page subtitle (NOTES §9) ----------------------------------------------------
  subtitleEditHint: 'Click any English text to fix it in place.',
  subtitleEsHint: 'Switch to English or Both to edit.',
  editsWaitForApproval: 'Edits wait for approval before employees see them.',
  editsGoLive: "Changes go to employees' phones as soon as they save.",

  // --- the run and the editor ----------------------------------------------------------
  srOpenHint: 'Press Enter to edit.',
  editLabel: (label: string) => `Edit ${label}`,
  keySave: 'save',
  keyCancel: 'cancel',
  keyNewline: 'Enter adds a line',
  save: 'Save',
  cancel: 'Cancel',
  markdownHint:
    'Markdown works here, the same as in Edit mode: **bold**, and a line starting with “- ” makes a list item.',
  emptyRequired: (label: string) => `${capitalize(label)} can't be empty. Press Esc to keep the original.`,

  // --- error box -------------------------------------------------------------------------
  yourTextIsStillHere: 'Your text is still here:',
  edit: 'Edit',
  discard: 'Discard',
  dismiss: 'Dismiss',
  undo: 'Undo',

  // --- mark correct (NOTES §7) ----------------------------------------------------------------
  markCorrect: (letter: string) => `Mark ${letter} correct`,
  isCorrect: (letter: string) => `${letter} is the correct answer`,
  correctNowIs: (letter: string) => `Correct answer is now ${letter}`,
  couldNotChangeCorrect: "Couldn't change the correct answer.",

  // --- pending markers (NOTES §8) ----------------------------------------------------------------
  pendingChip: 'Waiting for approval',
  pendingLabelUpdated: `Waiting for approval — employees still see the approved version. ${OPENS_APPROVALS}`,
  pendingLabelCreated: `Waiting for approval — employees can't see this yet. ${OPENS_APPROVALS}`,
  notLiveLabel: `${APPROVAL_EDITOR_COPY.treeChipNeverApprovedLabel}. ${OPENS_APPROVALS}`,
  opensApprovals: OPENS_APPROVALS,

  // --- placeholders for empty OPTIONAL fields (NOTES §6) ----------------------------------------------
  placeholderExplanation: 'Add wrong-answer feedback',
  placeholderCorrectExplanation: 'Add right-answer feedback',
  placeholderSubtitle: 'Add a subtitle',

  // --- empty REQUIRED long fields (QA-F2): same muted run, still required (AT-7) ------------------
  placeholderCriterionDescription: 'Add a description',
  placeholderScenario: 'Add the guest prompt',
  placeholderReadingBody: `Add ${T.reading.singular.toLowerCase()} text`,
} as const;

/**
 * Field labels — the `label` passed to `couldNotSaveField` and (capitalised) to
 * `emptyRequired`, and the textarea's `aria-label` (NOTES §9 "Field labels").
 * Functions, not literals, so every level word is a `QUIZ_TERMS` lookup.
 */
export const FIELD_LABELS = {
  question_text: () => 'the question',
  option: (letter: string) => `choice ${letter}`,
  explanation: () => 'the wrong-answer feedback',
  correct_explanation: () => 'the right-answer feedback',
  readingTitle: () => `the ${T.reading.singular} title`,
  readingBody: () => `the ${T.reading.singular} text`,
  rehearsalName: () => `the ${T.rehearsal.singular} name`,
  scenario: () => 'the guest prompt',
  criterionLabel: () => "this point's label",
  criterionDescription: () => "this point's description",
  lessonTitle: () => `the ${T.lesson.singular} title`,
  lessonSubtitle: () => `the ${T.lesson.singular} subtitle`,
  drillTitle: () => `the ${T.drill.singular} title`,
} as const;
